import { useMemo, useState } from "react";
import { Search, Paperclip, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KIND_LABEL, resolveStarters } from "./templates";

// The welcome screen: a few quiet chips and one link to everything else. Configurable per
// community in Settings → Capabilities → Ask Copilot starters.
export function StarterChips({ tenant, onPick }) {
  const [open, setOpen] = useState(false);
  const { chips, library } = useMemo(() => resolveStarters(tenant), [tenant]);
  return (
    <div data-testid="copilot-starters">
      <div className="flex flex-wrap justify-center gap-2">
        {chips.map((t) => (
          <button key={t.id} data-testid={`copilot-starter-${t.id}`} onClick={() => onPick(t)}
            className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-3.5 py-2 text-sm text-ink/75 hover:text-ink hover:border-ink/40 transition-colors whitespace-nowrap">
            <t.icon className={`h-3.5 w-3.5 shrink-0 ${t.kind === "draft" ? "text-redline-500" : "text-ink/45"}`} strokeWidth={2} />
            {t.label}
            {t.needsFile && <Paperclip className="h-3 w-3 text-ink/30" />}
          </button>
        ))}
      </div>
      {library && (
        <div className="text-center mt-3">
          <button data-testid="copilot-starters-all" onClick={() => setOpen(true)} className="text-xs font-semibold text-ink/45 hover:text-ink transition-colors inline-flex items-center gap-1">
            All starters <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      )}
      <StarterLibrary open={open} onOpenChange={setOpen} tenant={tenant} onPick={(t) => { setOpen(false); onPick(t); }} />
    </div>
  );
}

export function StarterLibrary({ open, onOpenChange, tenant, onPick }) {
  const [q, setQ] = useState("");
  const { all } = useMemo(() => resolveStarters(tenant), [tenant]);
  const needle = q.trim().toLowerCase();
  const shown = needle ? all.filter((t) => `${t.label} ${t.prompt}`.toLowerCase().includes(needle)) : all;
  const kinds = ["ask", "draft", "analyze", "custom"].filter((k) => shown.some((t) => t.kind === k));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="copilot-starter-library">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="font-heading">Starters</DialogTitle>
          <DialogDescription>Pick one — it lands in the box with blanks to fill before you send.</DialogDescription>
        </DialogHeader>
        <div className="px-5 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/35" />
            <input autoFocus data-testid="copilot-starter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search starters…"
              className="w-full rounded-full border border-sand-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-ink/40" />
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto px-3 pb-4 chat-scroll">
          {kinds.length === 0 && <div className="px-3 py-8 text-center text-sm text-ink/50">Nothing matches.</div>}
          {kinds.map((k) => (
            <div key={k} className="mb-2">
              <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink/40">{KIND_LABEL[k]}</div>
              {shown.filter((t) => t.kind === k).map((t) => (
                <button key={t.id} data-testid={`copilot-library-${t.id}`} onClick={() => onPick(t)}
                  className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm text-ink/80 hover:bg-sand-50 hover:text-ink transition-colors">
                  <t.icon className={`h-4 w-4 shrink-0 ${k === "draft" ? "text-redline-500" : "text-ink/45"}`} strokeWidth={2} />
                  <span className="flex-1 min-w-0 truncate">{t.label}</span>
                  {t.needsFile && <Paperclip className="h-3.5 w-3.5 text-ink/30 shrink-0" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
